<template>
  <a-page
    :title="$t('rate_change_log')"
    :image_source="image_source"
    :image_height="image_height"
  >
    <template v-slot:header_options>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            class="mr-2"
            :loading="downloading"
            :disabled="!items.length"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="exportExcel(false)">
            <v-list-item-icon class="mr-3">
              <v-icon small>mdi-file-excel-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t("rate_change_log_download_without") }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="exportExcel(true)">
            <v-list-item-icon class="mr-3">
              <v-icon small>mdi-file-tree-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t("rate_change_log_download_with") }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn icon class="mr-2" :loading="loading" @click="reload">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </template>

    <v-card class="rcl-card" flat>
      <!-- Filter bar -->
      <div class="rcl-filters">
        <div class="d-flex flex-wrap align-center" style="gap: 10px">
          <v-menu
            v-model="menu_range"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="rangeLabel"
                :label="$t('date_range')"
                append-icon="mdi-calendar-blank-outline"
                readonly
                dense
                outlined
                rounded
                hide-details
                class="rcl-input rcl-range"
                v-on="on"
              ></v-text-field>
            </template>
            <v-card>
              <v-date-picker
                v-model="range"
                range
                :first-day-of-week="1"
                @change="onRangeChange"
              ></v-date-picker>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text small @click="clearRange">{{ $t("clear") }}</v-btn>
                <v-btn text small color="secondary" @click="menu_range = false">
                  {{ $t("ok") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <v-menu
            v-model="menu_stay_range"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="stayRangeLabel"
                :label="$t('rate_change_log_target_day')"
                append-icon="mdi-calendar-star-outline"
                readonly
                dense
                outlined
                rounded
                hide-details
                class="rcl-input rcl-range"
                v-on="on"
              ></v-text-field>
            </template>
            <v-card>
              <v-date-picker
                v-model="stay_range"
                range
                :first-day-of-week="1"
                @change="onStayRangeChange"
              ></v-date-picker>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text small @click="clearStayRange">{{
                  $t("clear")
                }}</v-btn>
                <v-btn
                  text
                  small
                  color="secondary"
                  @click="menu_stay_range = false"
                >
                  {{ $t("ok") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <v-select
            v-model="filters.user_id"
            :items="userOptions"
            :label="$t('rate_change_log_who')"
            dense
            outlined
            rounded
            hide-details
            clearable
            class="rcl-input rcl-select"
            @change="applyFilters"
          ></v-select>

          <v-select
            v-model="filters.category"
            :items="categoryOptions"
            :label="$t('category')"
            dense
            outlined
            rounded
            hide-details
            clearable
            class="rcl-input rcl-select"
            @change="applyFilters"
          ></v-select>

          <v-select
            v-model="filters.origin"
            :items="originOptions"
            :label="$t('rate_change_log_source')"
            dense
            outlined
            rounded
            hide-details
            clearable
            class="rcl-input rcl-select"
            @change="applyFilters"
          ></v-select>

          <v-btn
            outlined
            rounded
            height="40"
            class="rcl-input rcl-incidents"
            :class="{ 'rcl-incidents--on': filters.with_incidents }"
            @click="toggleIncidents"
          >
            <v-icon small left>mdi-alert-circle-outline</v-icon>
            {{ $t("rate_change_log_with_incidents") }}
          </v-btn>
        </div>
      </div>

      <div class="rcl-table-wrap">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :server-items-length="total"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          show-expand
          single-expand
          :expanded.sync="expanded"
          item-key="id"
          dense
          class="rcl-table elevation-0"
          @item-expanded="onItemExpanded"
        >
          <!-- REALIZADO -->
          <template v-slot:[`item.realizado`]="{ item }">
            <div class="rcl-when">
              <span class="rcl-when-day">{{
                formatDay(item.commit_date)
              }}</span>
              <span class="rcl-when-time">{{ item.commit_time }}</span>
            </div>
          </template>

          <!-- DÍA OBJETIVO -->
          <template v-slot:[`item.date`]="{ item }">
            {{ formatStayDate(item.date) }}
          </template>

          <!-- TARIFA -->
          <template v-slot:[`item.rate_plan_name`]="{ item }">
            <v-tooltip v-if="rateCode(item)" top>
              <template v-slot:activator="{ on, attrs }">
                <span class="rcl-tag" v-bind="attrs" v-on="on">{{
                  rateCode(item)
                }}</span>
              </template>
              <span>{{ rateName(item) }}</span>
            </v-tooltip>
            <span v-else class="grey--text">—</span>
          </template>

          <!-- CATEGORÍA -->
          <template v-slot:[`item.roomtype_name`]="{ item }">
            <span class="rcl-category">
              <span
                class="rcl-dot"
                :style="{ background: item.roomtype_color || '#bdbdbd' }"
              ></span>
              {{ item.roomtype_name || "—" }}
            </span>
          </template>

          <!-- CAMBIO -->
          <template v-slot:[`item.cambio`]="{ item }">
            <!-- Minimum / maximum stay: nights, not a price -->
            <span v-if="!isPriceChange(item)" class="rcl-change">
              <span class="rcl-old-kpi rcl-nights">
                {{ hasValue(item.ini_value) ? nights(item.ini_value) : "" }}
              </span>
              <v-icon x-small class="mx-1 grey--text">mdi-arrow-right</v-icon>
              <span class="rcl-new-kpi rcl-nights">
                <b>{{
                  hasValue(item.end_value) ? nights(item.end_value) : ""
                }}</b>
              </span>
              <span class="rcl-tag ml-2">{{ typeLabel(item.type) }}</span>
            </span>
            <span v-else class="rcl-change">
              <a-kpi
                v-if="hasValue(item.ini_value)"
                kpi="price"
                :value="num(item.ini_value)"
                class="rcl-old-kpi"
                absolute
                small
                left
              ></a-kpi>
              <v-icon x-small class="mx-1 grey--text">mdi-arrow-right</v-icon>
              <a-kpi
                v-if="hasValue(item.end_value)"
                kpi="price"
                :value="num(item.end_value)"
                class="rcl-new-kpi"
                absolute
                small
                left
              ></a-kpi>
              <a-kpi
                v-if="hasValue(item.end_value) && hasValue(item.ini_value)"
                kpi="price"
                :value="num(item.end_value)"
                :vs_values="[num(item.ini_value)]"
                class="ml-2"
                absolute
                hide_kpi
                hide_tooltip
                small
              ></a-kpi>
            </span>
          </template>

          <!-- ORIGEN -->
          <template v-slot:[`item.origin`]="{ item }">
            <span class="rcl-origin">
              <span
                class="rcl-dot"
                :style="{ background: originMeta(item.origin).color }"
              ></span>
              {{ originMeta(item.origin).label }}
              <span v-if="sourceLabel(item.source)" class="rcl-source">
                · {{ sourceLabel(item.source) }}
              </span>
            </span>
          </template>

          <!-- USUARIO -->
          <template v-slot:[`item.user_name`]="{ item }">
            <div class="rcl-user">
              <span
                class="rcl-avatar"
                :style="{ background: avatarColor(displayUser(item)) }"
              >
                {{ initials(displayUser(item)) }}
              </span>
              <span class="rcl-user-name">{{ displayUser(item) }}</span>
            </div>
          </template>

          <!-- SINCRONIZACIÓN -->
          <template v-slot:[`item.sync`]="{ item }">
            <div class="rcl-sync">
              <span class="rcl-badge" :title="syncTitle('pms', item)">
                <span
                  class="rcl-dot"
                  :style="{ background: syncColor(item.pms_sync_status) }"
                ></span>
                PMS
              </span>
              <!-- Only price changes get a snapshot -->
              <span
                v-if="isPriceChange(item)"
                class="rcl-badge"
                :title="syncTitle('snapshot', item)"
              >
                <span
                  class="rcl-dot"
                  :style="{ background: syncColor(item.snapshot_status) }"
                ></span>
                SNAP
              </span>
            </div>
          </template>

          <!-- RECÁLCULOS -->
          <template v-slot:[`item.cascade_count`]="{ item }">
            <span v-if="hasCascade(item)" class="rcl-recalc-group">
              <span
                v-if="item.cascade_count > 0"
                class="rcl-recalc"
                :class="{ 'rcl-recalc--active': isExpanded(item) }"
                :title="$t('rate_change_log_rules_detail')"
                @click.stop="toggleRow(item)"
              >
                <v-icon x-small>mdi-sync</v-icon>
                {{ item.cascade_count }}
              </span>
              <span
                v-if="item.download_count > 0"
                class="rcl-recalc rcl-recalc--download"
                :class="{ 'rcl-recalc--active': isExpanded(item) }"
                :title="$t('rate_change_log_downloads_title')"
                @click.stop="toggleRow(item)"
              >
                <v-icon x-small>mdi-cloud-download-outline</v-icon>
                {{ item.download_count }}
              </span>
            </span>
            <span v-else class="grey--text">0</span>
          </template>

          <!-- ACCIONES -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="rcl-actions">
              <v-btn
                v-if="hasIncident(item)"
                icon
                x-small
                color="error"
                :loading="retrying_id === item.id"
                :title="$t('retry_pms_sync')"
                @click.stop="retry(item)"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Expand toggle only when there are recalculations -->
          <template v-slot:[`item.data-table-expand`]="{ item }">
            <v-btn
              v-if="hasCascade(item)"
              icon
              x-small
              class="rcl-expand-btn"
              @click.stop="toggleRow(item)"
            >
              <v-icon>{{
                isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
          </template>

          <!-- EXPANDED: cascade panel -->
          <template v-slot:expanded-item="{ headers: cols, item }">
            <td :colspan="cols.length" class="rcl-expanded pa-0">
              <div class="pa-4">
                <div
                  v-if="detailLoading && expandedItemId === item.id"
                  class="py-2"
                >
                  <v-progress-linear
                    indeterminate
                    color="secondary"
                  ></v-progress-linear>
                </div>

                <template v-else-if="detailFor(item)">
                  <div
                    class="rcl-cascade-head d-flex flex-wrap align-center mb-2"
                  >
                    <span class="rcl-cascade-title">
                      {{ $t("rate_change_log_cascade_detail") }} ·
                      {{ cascadeRows(item).length }}
                    </span>
                    <span class="rcl-meta ml-4">
                      {{ $t("rate_change_log_rules_detail") }}:
                      {{ cascadeGroups(item)[0].rows.length }} ·
                      {{ $t("rate_change_log_downloads_detail") }}:
                      {{ cascadeGroups(item)[1].rows.length }}
                    </span>
                    <span v-if="item.snapshot_done_at" class="rcl-meta ml-4">
                      <v-icon x-small class="mr-1"
                        >mdi-content-save-outline</v-icon
                      >
                      {{ $t("rate_change_log_snapshot_saved") }} ·
                      {{ timeOnly(item.snapshot_done_at) }}
                    </span>
                    <span
                      v-if="item.pms_sync_status === 'synced'"
                      class="rcl-meta ml-4"
                    >
                      <span class="rcl-dot" style="background:#2e9e6b"></span>
                      {{ $t("rate_change_log_pms_synced_note") }}
                    </span>
                  </div>

                  <div
                    v-if="detailFor(item).change.pms_last_error"
                    class="error--text caption mb-3"
                  >
                    <strong>{{ $t("rate_change_log_pms_error") }}:</strong>
                    {{ detailFor(item).change.pms_last_error }}
                  </div>

                  <table
                    v-for="group in cascadeGroups(item).filter(
                      g => g.rows.length
                    )"
                    :key="group.key"
                    class="rcl-cascade-table mb-3"
                  >
                    <caption class="rcl-cascade-caption">
                      <v-icon x-small class="mr-1">{{ group.icon }}</v-icon>
                      {{
                        group.title
                      }}
                      ·
                      {{
                        group.rows.length
                      }}
                      <span class="rcl-meta ml-2">{{ group.hint }}</span>
                    </caption>
                    <thead>
                      <tr>
                        <th
                          v-for="col in cascadeColumns"
                          :key="col.key"
                          class="rcl-sortable"
                          :class="{ 'text-right': col.align === 'end' }"
                          @click="sortCascade(col.key)"
                        >
                          {{ col.label }}
                          <v-icon
                            v-if="cascade_sort.by === col.key"
                            x-small
                            class="rcl-sort-icon"
                          >
                            {{
                              cascade_sort.desc
                                ? "mdi-arrow-down"
                                : "mdi-arrow-up"
                            }}
                          </v-icon>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="child in group.rows" :key="child.id">
                        <td>
                          <span class="rcl-category">
                            <span
                              class="rcl-dot"
                              :style="{
                                background: child.roomtype_color || '#bdbdbd'
                              }"
                            ></span>
                            {{
                              child.roomtype_name || child.roomtype_code || "—"
                            }}
                          </span>
                        </td>
                        <td>{{ paxLabel(child.occupancy) }}</td>
                        <td>
                          <span class="rcl-change">
                            <a-kpi
                              v-if="hasValue(child.ini_value)"
                              kpi="price"
                              :value="num(child.ini_value)"
                              class="rcl-old-kpi"
                              absolute
                              small
                              left
                            ></a-kpi>
                            <v-icon x-small class="mx-1 grey--text"
                              >mdi-arrow-right</v-icon
                            >
                            <a-kpi
                              v-if="hasValue(child.end_value)"
                              kpi="price"
                              :value="num(child.end_value)"
                              class="rcl-new-kpi"
                              absolute
                              small
                              left
                            ></a-kpi>
                            <a-kpi
                              v-if="
                                hasValue(child.end_value) &&
                                  hasValue(child.ini_value)
                              "
                              kpi="price"
                              :value="num(child.end_value)"
                              :vs_values="[num(child.ini_value)]"
                              class="ml-2"
                              absolute
                              hide_kpi
                              hide_tooltip
                              small
                            ></a-kpi>
                          </span>
                        </td>
                        <td class="grey--text text--darken-1">
                          {{ childReason(child) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p
                    v-if="!cascadeRows(item).length"
                    class="grey--text caption mb-0"
                  >
                    {{ $t("rate_change_log_no_cascade") }}
                  </p>
                </template>
              </div>
            </td>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </a-page>
</template>

<script>
import aPage from "@/components/aPage";
import aKpi from "@/components/rms_ui/aKpi";
import { showError } from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import * as XLSX from "xlsx";
var numeral = require("numeral");

// Converts a binary string into an ArrayBuffer for the Excel blob.
function s2ab(s) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) {
    view[i] = s.charCodeAt(i) & 0xff;
  }
  return buf;
}

// Sources of values downloaded from the PMS/CM (RmsChangelog::getDownloadSources()).
const DOWNLOAD_SOURCE_NAMES = {
  ulyses_download: "Ulyses",
  siteminder_download: "Siteminder",
  fnsrooms_download: "FNS Rooms"
};
const DOWNLOAD_SOURCES = Object.keys(DOWNLOAD_SOURCE_NAMES);

const AVATAR_COLORS = [
  "#7b4fa3",
  "#2e9e6b",
  "#c0563b",
  "#1576c7",
  "#b8860b",
  "#864d7d",
  "#3a7d44",
  "#a8432f"
];

export default {
  name: "rate-change-log",
  components: { aPage, aKpi },
  data: () => ({
    image_source: "/img/header.jpg",
    image_height: 200,
    menu_range: false,
    range: [],
    menu_stay_range: false,
    stay_range: [],
    downloading: false,
    cascade_sort: { by: null, desc: false },
    filters: {
      from: null,
      to: null,
      stay_from: null,
      stay_to: null,
      user_id: null,
      category: null,
      origin: null,
      with_incidents: null
    },
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: []
    },
    expanded: [],
    expandedItemId: null,
    retrying_id: null,
    poll_timer: null,
    seenCategories: []
  }),
  computed: {
    ...mapState({
      list_status: state => state.rateChangeLog.list_status,
      items: state => state.rateChangeLog.list,
      total: state => state.rateChangeLog.list_total,
      detail: state => state.rateChangeLog.detail,
      detail_status: state => state.rateChangeLog.detail_status,
      users: state => state.user.users
    }),
    ...mapGetters([
      "current_hotel",
      "current_hotel_language",
      "rateChangeLogHasPending"
    ]),
    l() {
      return this.current_hotel_language;
    },
    loading() {
      return this.list_status === 1;
    },
    detailLoading() {
      return this.detail_status === 1;
    },
    headers() {
      return [
        {
          text: this.$t("rate_change_log_done"),
          value: "realizado"
        },
        {
          text: this.$t("rate_change_log_target_day"),
          value: "date"
        },
        {
          text: this.$t("rate_plan"),
          value: "rate_plan_name"
        },
        { text: this.$t("category"), value: "roomtype_name" },
        {
          text: this.$t("rate_change_log_change"),
          value: "cambio"
        },
        {
          text: this.$t("rate_change_log_source"),
          value: "origin"
        },
        {
          text: this.$t("rate_change_log_who"),
          value: "user_name"
        },
        {
          text: this.$t("rate_change_log_sync"),
          value: "sync",
          sortable: false
        },
        {
          text: this.$t("rate_change_log_cascade"),
          value: "cascade_count",
          align: "end",
          width: 96
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          width: 40,
          align: "end"
        },
        { text: "", value: "data-table-expand", sortable: false, width: 36 }
      ];
    },
    userOptions() {
      const hotelId = this.current_hotel && this.current_hotel.id;
      return (this.users || [])
        .filter(u => this.userHasCurrentHotelAccess(u, hotelId))
        .map(u => ({
          value: u.id,
          text: this.fullName(u)
        }));
    },
    categoryOptions() {
      return this.seenCategories.map(c => ({ text: c, value: c }));
    },
    originOptions() {
      return ["manual", "suggestion", "automatic"].map(value => ({
        value,
        text: this.originMeta(value).label
      }));
    },
    rangeLabel() {
      if (this.filters.from && this.filters.to) {
        return `${this.formatDay(this.filters.from)} – ${this.formatDay(
          this.filters.to
        )}`;
      }
      if (this.filters.from) return this.formatDay(this.filters.from);
      return this.$t("rate_change_log_all_dates");
    },
    stayRangeLabel() {
      if (this.filters.stay_from && this.filters.stay_to) {
        return `${this.formatDay(this.filters.stay_from)} – ${this.formatDay(
          this.filters.stay_to
        )}`;
      }
      if (this.filters.stay_from) return this.formatDay(this.filters.stay_from);
      return this.$t("rate_change_log_all_dates");
    },
    cascadeColumns() {
      return [
        { key: "category", label: this.$t("category") },
        { key: "occupancy", label: this.$t("rate_change_log_occupancy") },
        { key: "change", label: this.$t("rate_change_log_change") },
        { key: "reason", label: this.$t("rate_change_log_reason") }
      ];
    }
  },
  created() {
    // Default to today's changes.
    const today = Vue.moment().format("YYYY-MM-DD");
    this.filters.from = today;
    this.filters.to = today;
    this.range = [today, today];
  },
  mounted() {
    this._last_sort = JSON.stringify([
      this.options.sortBy,
      this.options.sortDesc
    ]);
    if (!this.users || !this.users.length) {
      this.$store.dispatch("fetchUsers");
    }
    this.loadList();
  },
  beforeDestroy() {
    this.stopPolling();
    this.$store.dispatch("clearRateChangeLogDetail");
  },
  watch: {
    current_hotel() {
      this.options.page = 1;
      this.expanded = [];
      this.seenCategories = [];
      // Drop the selected user if it isn't visible for the new hotel.
      if (
        this.filters.user_id &&
        !this.userOptions.some(o => o.value === this.filters.user_id)
      ) {
        this.filters.user_id = null;
      }
      this.queueLoad();
    },
    // Reacts to pagination, items-per-page and sorting changes from the table.
    options: {
      handler() {
        const sig = JSON.stringify([
          this.options.sortBy,
          this.options.sortDesc
        ]);
        // When the sort criteria changes, jump back to the first page so the
        // user isn't stranded on a now-out-of-range page of the new ordering.
        if (this._last_sort !== sig && this.options.page !== 1) {
          this._last_sort = sig;
          this.options.page = 1;
          return;
        }
        this._last_sort = sig;
        this.queueLoad();
      },
      deep: true
    },
    rateChangeLogHasPending(hasPending) {
      if (hasPending) this.startPolling();
      else this.stopPolling();
    },
    items() {
      this.collectCategories();
    }
  },
  methods: {
    loadList() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      this.$store.dispatch("setRateChangeLogFilters", {
        ...this.filters,
        page,
        per_page: itemsPerPage,
        sort_by: sortBy.length ? this.mapSortKey(sortBy[0]) : null,
        sort_desc: sortDesc.length ? sortDesc[0] : null
      });
      this.$store
        .dispatch("fetchRateChangeLogList")
        .then(() => {
          if (this.rateChangeLogHasPending) this.startPolling();
        })
        .catch(() => showError(this, "something_went_wrong"));
    },
    // Coalesce reloads triggered synchronously by v-data-table (a header click
    // can update both sortBy and sortDesc) into a single fetch.
    queueLoad() {
      if (this._load_queued) return;
      this._load_queued = true;
      this.$nextTick(() => {
        this._load_queued = false;
        this.loadList();
      });
    },
    // Maps a v-data-table column key to the backend sort field. "cambio" sorts
    // by the actual price increment and "realizado" by the real commit
    // timestamp, not the rendered text.
    mapSortKey(column) {
      const map = {
        realizado: "commit_datetime",
        date: "date",
        rate_plan_name: "rate_plan_name",
        roomtype_name: "roomtype_name",
        cambio: "increment",
        origin: "origin",
        user_name: "user_name",
        cascade_count: "cascade_count"
      };
      return map[column] || column;
    },
    reload() {
      this.loadList();
    },
    applyFilters() {
      this.options.page = 1;
      this.queueLoad();
    },
    onRangeChange(value) {
      const sorted = [...value].sort();
      this.filters.from = sorted[0] || null;
      this.filters.to = sorted[1] || sorted[0] || null;
      this.applyFilters();
    },
    clearRange() {
      this.range = [];
      this.filters.from = null;
      this.filters.to = null;
      this.menu_range = false;
      this.applyFilters();
    },
    toggleIncidents() {
      this.filters.with_incidents = this.filters.with_incidents ? null : 1;
      this.applyFilters();
    },
    onStayRangeChange(value) {
      const sorted = [...value].sort();
      this.filters.stay_from = sorted[0] || null;
      this.filters.stay_to = sorted[1] || sorted[0] || null;
      this.applyFilters();
    },
    clearStayRange() {
      this.stay_range = [];
      this.filters.stay_from = null;
      this.filters.stay_to = null;
      this.menu_stay_range = false;
      this.applyFilters();
    },
    rateCode(item) {
      return item.rate_plan_code.toUpperCase() || item.rate_plan_name || "";
    },
    rateName(item) {
      return item.rate_plan_name || item.rate_plan_code || "";
    },
    onItemExpanded({ item, value }) {
      if (value) {
        this.expandedItemId = item.id;
        this.$store
          .dispatch("fetchRateChangeLogDetail", item.id)
          .catch(() => showError(this, "something_went_wrong"));
      } else {
        this.expandedItemId = null;
        this.$store.dispatch("clearRateChangeLogDetail");
      }
    },
    // Toggle expansion from either the chevron or the recalcs badge.
    toggleRow(item) {
      if (this.isExpanded(item)) {
        this.expanded = [];
        this.onItemExpanded({ item, value: false });
      } else {
        this.expanded = [item];
        this.onItemExpanded({ item, value: true });
      }
    },
    retry(item) {
      this.retrying_id = item.id;
      const action =
        item.snapshot_status === "error" && item.pms_sync_status !== "error"
          ? "retryRateChangeLogSnapshot"
          : "retryRateChangeLogPms";
      this.$store
        .dispatch(action, item.id)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("retry_pms_sync_success"),
            color: "accent"
          });
          this.startPolling();
        })
        .catch(() => showError(this, "retry_pms_sync_error"))
        .finally(() => {
          this.retrying_id = null;
        });
    },
    startPolling() {
      if (this.poll_timer) return;
      this.poll_timer = setInterval(() => {
        this.$store.dispatch("fetchRateChangeLogStatuses").catch(() => {});
        if (!this.rateChangeLogHasPending) this.stopPolling();
      }, 10000);
    },
    stopPolling() {
      if (this.poll_timer) {
        clearInterval(this.poll_timer);
        this.poll_timer = null;
      }
    },
    collectCategories() {
      const set = new Set(this.seenCategories);
      (this.items || []).forEach(i => {
        if (i.roomtype_name) set.add(i.roomtype_name);
      });
      this.seenCategories = Array.from(set).sort();
    },
    isExpanded(item) {
      return this.expanded.some(i => String(i.id) === String(item.id));
    },
    detailFor(item) {
      // List ids come from a raw query (strings); detail.change.id is an int
      // (ActiveRecord) — compare as strings to avoid a strict-equality miss.
      return this.detail &&
        this.detail.change &&
        String(this.detail.change.id) === String(item.id)
        ? this.detail
        : null;
    },
    cascadeRows(item) {
      const d = this.detailFor(item);
      if (!d) return [];
      return d.children || d.cascade || [];
    },
    hasCascade(item) {
      return item.cascade_count > 0 || item.download_count > 0;
    },
    // Values written by a PMS/CM download (not by a pricing rule).
    isDownload(child) {
      return DOWNLOAD_SOURCES.includes(child.source);
    },
    pmsName(child) {
      return DOWNLOAD_SOURCE_NAMES[child.source] || "PMS";
    },
    childReason(child) {
      if (this.isDownload(child)) {
        return this.$t("rate_change_log_reason_download", {
          pms: this.pmsName(child)
        });
      }
      return this.reasonText(child.recalc_reason);
    },
    // Children split in two blocks: rule recalculations first, then the values
    // that came back from the PMS in the download that follows each sync.
    cascadeGroups(item) {
      const rows = this.sortedCascadeRows(item);
      return [
        {
          key: "rules",
          icon: "mdi-sync",
          title: this.$t("rate_change_log_rules_detail"),
          hint: this.$t("rate_change_log_rules_hint"),
          rows: rows.filter(c => !this.isDownload(c))
        },
        {
          key: "downloads",
          icon: "mdi-cloud-download-outline",
          title: this.$t("rate_change_log_downloads_detail"),
          hint: this.$t("rate_change_log_downloads_title"),
          rows: rows.filter(c => this.isDownload(c))
        }
      ];
    },
    // Sort value for a cascade child given the active sort column.
    cascadeSortValue(child, key) {
      if (key === "occupancy") return Number(child.occupancy) || 0;
      if (key === "change") {
        return (Number(child.end_value) || 0) - (Number(child.ini_value) || 0);
      }
      if (key === "category") {
        return (child.roomtype_name || child.roomtype_code || "").toLowerCase();
      }
      if (key === "reason") return this.childReason(child);
      return "";
    },
    sortedCascadeRows(item) {
      const rows = this.cascadeRows(item).slice();
      const { by, desc } = this.cascade_sort;
      if (!by) return rows;
      rows.sort((a, b) => {
        const va = this.cascadeSortValue(a, by);
        const vb = this.cascadeSortValue(b, by);
        if (va < vb) return desc ? 1 : -1;
        if (va > vb) return desc ? -1 : 1;
        return 0;
      });
      return rows;
    },
    sortCascade(key) {
      if (this.cascade_sort.by === key) {
        this.cascade_sort.desc = !this.cascade_sort.desc;
      } else {
        this.cascade_sort.by = key;
        this.cascade_sort.desc = false;
      }
    },
    // --- formatting helpers ---
    fullName(u) {
      return `${u.name || ""} ${u.surnames || ""}`.trim() || `#${u.id}`;
    },
    // Mirrors the Team screen visibility model: a user is listed only when the
    // current hotel node in their `hotel` map is flagged with access.
    userHasCurrentHotelAccess(user, hotelId) {
      if (!hotelId || !user || !user.hotel) return true;
      return Object.values(user.hotel).some(
        h => h && h.has_access && String(h.id) === String(hotelId)
      );
    },
    displayUser(item) {
      if (item.user_name) return item.user_name;
      if (item.origin === "automatic")
        return this.$t("rate_change_log_automatic_user");
      return "—";
    },
    initials(name) {
      if (!name || name === "—") return "?";
      const parts = name.trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const second = parts[1] ? parts[1][0] : "";
      return (first + second).toUpperCase();
    },
    avatarColor(name) {
      const key = name || "";
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash);
      }
      return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    },
    originMeta(origin) {
      const map = {
        manual: {
          label: this.$t("rate_change_log_origin_manual"),
          color: "#6b7280"
        },
        suggestion: {
          label: this.$t("rate_change_log_origin_suggestion"),
          color: "#1576c7"
        },
        automatic: {
          label: this.$t("rate_change_log_origin_automatic"),
          color: "#2e9e6b"
        }
      };
      return map[origin] || { label: origin || "—", color: "#bdbdbd" };
    },
    isPriceChange(item) {
      return !item.type || item.type === "price";
    },
    typeLabel(type) {
      const map = {
        min_los: "MLOS",
        max_los: "MaxLOS"
      };
      return map[type] || type;
    },
    nights(value) {
      return `${this.num(value)} ${this.$t("rate_change_log_nights")}`;
    },
    // Which screen or tool the change was made from
    sourceLabel(source) {
      const map = {
        strategy_day: "rate_change_log_source_day",
        pricing_day: "rate_change_log_source_day",
        strategy_massive: "rate_change_log_source_massive",
        pricing_massive: "rate_change_log_source_massive",
        pricing_bulk_upload: "rate_change_log_source_bulk_upload",
        ref_recalc: "rate_change_log_source_ref_recalc"
      };
      return map[source] ? this.$t(map[source]) : "";
    },
    syncColor(status) {
      const colors = {
        synced: "#2e9e6b",
        done: "#2e9e6b",
        pending: "#e0a32e",
        error: "#d6463b",
        skipped: "#bdbdbd"
      };
      return colors[status] || "#d0d5dd";
    },
    syncTitle(kind, item) {
      const status =
        kind === "pms" ? item.pms_sync_status : item.snapshot_status;
      const key = kind === "pms" ? "pms_sync_" : "snapshot_";
      return status ? this.$t(key + status) : "—";
    },
    hasIncident(item) {
      return (
        item.pms_sync_status === "error" || item.snapshot_status === "error"
      );
    },
    formatDay(date) {
      if (!date) return "";
      return Vue.moment(date)
        .format("D MMM")
        .toLowerCase();
    },
    formatStayDate(date) {
      if (!date) return "—";
      return Vue.moment(date)
        .format("D MMM YYYY")
        .toLowerCase();
    },
    timeOnly(datetime) {
      if (!datetime) return "";
      return Vue.moment(datetime).format("HH:mm:ss");
    },
    paxLabel(occupancy) {
      if (!occupancy) return "—";
      return `${occupancy} pax`;
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    num(value) {
      return Number(value);
    },
    reasonText(raw) {
      if (!raw) return "—";
      let r = raw;
      if (typeof raw === "string") {
        try {
          r = JSON.parse(raw);
        } catch (e) {
          return raw;
        }
      }
      if (!r || !r.type) return "—";
      if (r.type === "pax") {
        return this.$t("rate_change_log_reason_pax", {
          value: this.signedValue(r.value, "absolute")
        });
      }
      if (r.type === "related") {
        return this.$t("rate_change_log_reason_related", {
          value: this.signedValue(r.value, r.op),
          ref: r.ref_name || ""
        });
      }
      return "—";
    },
    signedValue(value, op) {
      const n = Number(value) || 0;
      const sign = n > 0 ? "+" : "";
      if (op === "percent") {
        return sign + numeral(n).format(this.l.number0_format) + "%";
      }
      return (
        sign +
        numeral(n).format(this.l.number0_format) +
        this.l.money_post_symbol
      );
    },
    increment(item) {
      if (!this.hasValue(item.ini_value) || !this.hasValue(item.end_value)) {
        return null;
      }
      return this.num(item.end_value) - this.num(item.ini_value);
    },
    exportRow(item) {
      return {
        [this.$t("rate_change_log_export_type")]: this.$t(
          "rate_change_log_export_main"
        ),
        [this.$t("rate_change_log_done")]: `${item.commit_date ||
          ""} ${item.commit_time || ""}`.trim(),
        [this.$t("rate_change_log_target_day")]: item.date || "",
        [this.$t("rate_plan")]: this.rateName(item),
        [this.$t("category")]: item.roomtype_name || "",
        [this.$t("rate_change_log_occupancy")]: "",
        [this.$t("rate_change_log_kpi")]: this.isPriceChange(item)
          ? this.$t("price")
          : this.typeLabel(item.type),
        [this.$t("rate_change_log_initial")]: this.hasValue(item.ini_value)
          ? this.num(item.ini_value)
          : "",
        [this.$t("rate_change_log_final")]: this.hasValue(item.end_value)
          ? this.num(item.end_value)
          : "",
        [this.$t("rate_change_log_increment")]:
          this.increment(item) === null ? "" : this.increment(item),
        [this.$t("rate_change_log_reason")]: "",
        [this.$t("rate_change_log_source")]: [
          this.originMeta(item.origin).label,
          this.sourceLabel(item.source)
        ]
          .filter(Boolean)
          .join(" · "),
        [this.$t("rate_change_log_who")]: this.displayUser(item),
        ["PMS"]: this.syncTitle("pms", item),
        ["SNAP"]: this.isPriceChange(item)
          ? this.syncTitle("snapshot", item)
          : "",
        [this.$t("rate_change_log_cascade")]: item.cascade_count || 0,
        [this.$t("rate_change_log_downloads_detail")]: item.download_count || 0
      };
    },
    exportCascadeRow(item, child) {
      return {
        [this.$t("rate_change_log_export_type")]: this.isDownload(child)
          ? this.$t("rate_change_log_downloads_detail")
          : this.$t("rate_change_log_export_cascade"),
        [this.$t("rate_change_log_done")]: `${item.commit_date ||
          ""} ${item.commit_time || ""}`.trim(),
        [this.$t("rate_change_log_target_day")]: item.date || "",
        [this.$t("rate_plan")]: this.rateName(item),
        [this.$t("category")]: child.roomtype_name || child.roomtype_code || "",
        [this.$t("rate_change_log_occupancy")]: child.occupancy || "",
        [this.$t("rate_change_log_kpi")]: this.isPriceChange(child)
          ? this.$t("price")
          : this.typeLabel(child.type),
        [this.$t("rate_change_log_initial")]: this.hasValue(child.ini_value)
          ? this.num(child.ini_value)
          : "",
        [this.$t("rate_change_log_final")]: this.hasValue(child.end_value)
          ? this.num(child.end_value)
          : "",
        [this.$t("rate_change_log_increment")]:
          this.increment(child) === null ? "" : this.increment(child),
        [this.$t("rate_change_log_reason")]: this.childReason(child),
        [this.$t("rate_change_log_source")]: "",
        [this.$t("rate_change_log_who")]: "",
        ["PMS"]: "",
        ["SNAP"]: "",
        [this.$t("rate_change_log_cascade")]: "",
        [this.$t("rate_change_log_downloads_detail")]: ""
      };
    },
    async exportExcel(withCascade) {
      if (!this.items.length || this.downloading) return;
      this.downloading = true;
      try {
        const rows = this.items.slice();
        const details = {};
        if (withCascade) {
          const ids = rows.filter(r => this.hasCascade(r)).map(r => r.id);
          const results = await Promise.all(
            ids.map(id =>
              this.$store.dispatch("fetchRateChangeLogDetailById", id)
            )
          );
          ids.forEach((id, i) => {
            details[id] = results[i];
          });
        }

        const data = [];
        rows.forEach(item => {
          data.push(this.exportRow(item));
          if (withCascade) {
            const detail = details[item.id];
            const children =
              (detail && (detail.children || detail.cascade)) || [];
            children.forEach(child => {
              data.push(this.exportCascadeRow(item, child));
            });
          }
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          this.$t("rate_change_log")
        );
        const wbout = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "binary"
        });
        const blob = new Blob([s2ab(wbout)], {
          type: "application/octet-stream"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${this.$t("rate_change_log")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 0);
      } catch (e) {
        showError(this, "something_went_wrong");
      } finally {
        this.downloading = false;
      }
    }
  }
};
</script>

<style scoped>
.rcl-card {
  border-radius: 10px;
}
.rcl-filters {
  padding: 16px 20px;
}
/* Unify height of every top filter control */
.rcl-input >>> .v-input__control,
.rcl-input >>> .v-input__slot {
  min-height: 40px !important;
}
.rcl-input.v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  border-color: rgba(0, 0, 0, 0.24);
  color: #475467;
}
.rcl-range {
  max-width: 250px;
}
.rcl-range >>> input {
  cursor: pointer;
}
.rcl-select {
  max-width: 180px;
}
.rcl-incidents--on {
  border-color: #d6463b !important;
  color: #d6463b !important;
  background: #fdecea;
}
/* Table breathing room inside the card */
.rcl-table-wrap {
  padding: 0 12px 12px;
}
.rcl-table >>> th {
  font-size: 10px !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3 !important;
  white-space: nowrap;
}
.rcl-table >>> td {
  font-size: 13px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
/* Compact rows, but let the first column breathe */
.rcl-table >>> th:first-child,
.rcl-table >>> td:first-child {
  padding-left: 20px !important;
}
.rcl-table >>> th:last-child,
.rcl-table >>> td:last-child {
  padding-right: 16px !important;
}
.rcl-table >>> tbody td {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.rcl-when {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.rcl-when-day {
  font-weight: 600;
}
.rcl-when-time {
  font-size: 11px;
  color: #98a2b3;
}
.rcl-tag {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 6px;
  background: #eef1f4;
  font-size: 11px;
  font-weight: 600;
  color: #475467;
}
.rcl-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  flex: 0 0 auto;
}
.rcl-category,
.rcl-origin {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.rcl-source {
  margin-left: 4px;
  color: #6b7280;
}
.rcl-nights {
  font-size: 12px;
}
/* Fixed-width grid so the "old → new" prices and the increment badge line up
   across every row of the column. */
.rcl-change {
  display: inline-grid;
  grid-template-columns: 70px 26px 70px auto;
  align-items: center;
  white-space: nowrap;
}
.rcl-change >>> .rcl-old-kpi {
  justify-self: end;
}
.rcl-change >>> .rcl-new-kpi {
  justify-self: start;
}
/* a-kpi: strike-through muted "old" price, emphasized "new" price */
.rcl-old-kpi >>> .a-kpi {
  color: #98a2b3;
  text-decoration: line-through;
  font-weight: 400 !important;
}
.rcl-new-kpi >>> .a-kpi {
  font-weight: 600 !important;
}
.rcl-user {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.rcl-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  margin-right: 8px;
}
.rcl-sync {
  display: inline-flex;
  gap: 10px;
}
.rcl-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
}
.rcl-recalc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef2fb;
  color: #1576c7;
  font-weight: 600;
  font-size: 12px;
}
.rcl-recalc-group {
  display: inline-flex;
  gap: 4px;
}
.rcl-recalc--download {
  background: #eef3f7;
  color: #546e7a;
}
.rcl-cascade-caption {
  caption-side: top;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 0 6px;
}
.rcl-recalc--active {
  background: #1576c7;
  color: #fff;
}
.rcl-actions {
  display: inline-flex;
  gap: 2px;
  justify-content: flex-end;
}
.rcl-expanded {
  background: #f7f9fb !important;
}
.rcl-cascade-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475467;
}
.rcl-meta {
  font-size: 11px;
  color: #667085;
  display: inline-flex;
  align-items: center;
}
.rcl-cascade-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
}
.rcl-cascade-table th {
  text-align: left;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.rcl-cascade-table th.rcl-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.rcl-cascade-table th.rcl-sortable:hover {
  color: #667085;
}
.rcl-sort-icon {
  vertical-align: middle;
  margin-left: 2px;
}
.rcl-cascade-table td {
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}
.rcl-cascade-table tbody tr:last-child td {
  border-bottom: none;
}
</style>
